import React, { useEffect, useState } from "react";
import classes from "./home.module.css";
import { ARTIST_NEXT_DOOR_ARENA_ADVERT } from "../../../../utils/images";
import { useDispatch, useSelector } from "react-redux";
import { getArtistDashboardDetails } from "../../../../redux/slices/artistSlice";
import HowItWorksArtistModal from "../../../../components/HowItWorksArtistModal";
import { useNavigate } from "react-router-dom";
import ArenaIntroModal from "../../../../components/ArtistArenaModal";

const Home = ({ setActiveSection, artistProfile }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { artistDashboardDetails, artistDashboardDetailsLoading } = useSelector(
    (state) => state.artists
  );
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

  // Check if profile is complete (has photos and videos)
  const isProfileComplete = () => {
    if (!artistProfile) return false;

    const hasPhotos = artistProfile.photos && artistProfile.photos.length > 0;
    const hasVideos = artistProfile.videos && artistProfile.videos.length > 0;

    return hasPhotos && hasVideos;
  };

  // Check if profile is verified
  const isProfileVerified = () => {
    return artistProfile?.approved === true;
  };

  // Format currency for INR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get status class for bookings
  const getStatusClass = (status) => {
    switch (status) {
      case "Confirmed":
        return classes.statusConfirmed;
      case "Pending":
        return classes.statusPending;
      case "Cancelled":
        return classes.statusCancelled;
      default:
        return "";
    }
  };

  const handleViewProfile = () => {
    if (isProfileVerified()) {
      navigate(`/profile/${artistProfile?.username}`);
    } else {
      navigate(`/preview-profile/${artistProfile?.username}`);
    }
  };

  const handleArenaClick = () => {
    navigate(`/artist-next-door-arena`);
  };

  const handleCompleteProfile = () => {
    setActiveSection("preferences");
  };

  // Load artist dashboard details
  useEffect(() => {
    dispatch(getArtistDashboardDetails());
  }, [dispatch]);

  const handleViewAllBtn = () => {
    setActiveSection("bookings");
  };

  function formatDate(dateInput = new Date()) {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
    };

    return new Date(dateInput).toLocaleDateString("en-US", options);
  }

  const date = formatDate();

  // Define placeholder data for the initial render
  const placeholderData = {
    name: "",
    totalEarnings: 0,
    confirmedBookingsCount: 0,
    artistRating: 0,
    upcomingBookings: [],
  };

  // Use dashboard details if available, otherwise use placeholder
  const dashboardData = artistDashboardDetails || placeholderData;

  return (
    <div className={classes.dashboard}>
      {/* Arena Intro Modal - This will show automatically on first visit */}
      <ArenaIntroModal />

      {/* Welcome Section */}
      <section className={classes.welcomeSection}>
        <div className={classes.welcomeText}>
          <h1 className={classes.greeting}>
            Welcome back, {dashboardData.name || "Artist"}!
          </h1>
          <p className={classes.dashboardDate}>{date}</p>
        </div>
        <div className={classes.buttonsContainer}>
          <button
            className={classes.howItWorksButton}
            onClick={() => handleViewProfile()}
          >
            {isProfileVerified() ? "My Portfolio" : "Preview Portfolio"}
          </button>
          <button
            className={classes.howItWorksButton}
            onClick={() => setShowHowItWorksModal(true)}
          >
            How It Works
          </button>
        </div>
      </section>

      {/* Profile Completion Banner */}
      {!isProfileComplete() && (
        <section className={classes.profileCompletionBanner}>
          <div className={classes.bannerContent}>
            <div className={classes.bannerText}>
              <h3 className={classes.bannerTitle}>Complete Your Profile</h3>
              <p className={classes.bannerDescription}>
                Your profile is not complete. Add photos and videos to attract
                more bookings and showcase your talent.
              </p>
            </div>
            <button
              className={classes.completeProfileButton}
              onClick={handleCompleteProfile}
            >
              Complete Profile
            </button>
          </div>
        </section>
      )}

      {/* Profile Verification Banner */}
      {!isProfileVerified() && (
        <section className={classes.profileVerificationBanner}>
          <div className={classes.bannerContent}>
            <div className={classes.bannerText}>
              <h3 className={classes.bannerTitle}>
                Profile Under Verification
              </h3>
              <p className={classes.bannerDescription}>
                Your profile is currently under verification. Verification can
                take up to 48 hours. You can preview your profile, but it won't
                be public until verified.
              </p>
            </div>
            <button
              className={classes.previewProfileButton}
              onClick={handleViewProfile}
            >
              Preview Profile
            </button>
          </div>
        </section>
      )}

      {/* Stats Cards */}
      <section className={classes.statsCards}>
        <div
          className={`${classes.card} ${
            artistDashboardDetailsLoading ? classes.loadingCard : ""
          }`}
        >
          <div className={classes.cardContent}>
            <h2 className={classes.cardTitle}>Total Earnings</h2>
            <p className={classes.earningsAmount}>
              {formatCurrency(dashboardData.totalEarnings || 0)}
            </p>
            <p className={classes.earningsPeriod}>Financial Year 2025-26</p>
          </div>
          <div className={classes.cardIcon}>💰</div>
        </div>

        <div
          className={`${classes.card} ${
            artistDashboardDetailsLoading ? classes.loadingCard : ""
          }`}
        >
          <div className={classes.cardContent}>
            <h2 className={classes.cardTitle}>Upcoming Shows</h2>
            <p className={classes.statsNumber}>
              {dashboardData.confirmedBookingsCount || 0}
            </p>
            <p className={classes.statsPeriod}>Next 30 days</p>
          </div>
          <div className={classes.cardIcon}>🗓️</div>
        </div>

        <div
          className={`${classes.card} ${
            artistDashboardDetailsLoading ? classes.loadingCard : ""
          }`}
        >
          <div className={classes.cardContent}>
            <h2 className={classes.cardTitle}>Artist Rating</h2>
            <p className={classes.statsNumber}>
              {dashboardData.artistRating || 0}{" "}
              <span className={classes.ratingStars}>★★★★★</span>
            </p>
            <p className={classes.statsPeriod}>
              Based on {dashboardData.upcomingBookings?.length || 0} shows
            </p>
          </div>
          <div className={classes.cardIcon}>⭐</div>
        </div>
      </section>

      {/* Main Content */}
      <div className={classes.mainContent}>
        {/* Recent Bookings */}
        <section className={classes.bookingsSection}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>Recent Bookings</h2>
            <button
              className={classes.viewAllButton}
              onClick={handleViewAllBtn}
            >
              View All
            </button>
          </div>

          <div className={classes.bookingsList}>
            {artistDashboardDetailsLoading ? (
              // Placeholder cards while artistDashboardDetailsLoading
              Array(3)
                .fill(0)
                .map((_, index) => (
                  <div
                    key={index}
                    className={`${classes.bookingCard} ${classes.bookingCardPlaceholder}`}
                  >
                    <div className={classes.bookingHeader}>
                      <div
                        className={`${classes.bookingEvent} ${classes.placeholder}`}
                      ></div>
                      <div
                        className={`${classes.bookingStatus} ${classes.placeholder}`}
                      ></div>
                    </div>
                    <div className={classes.bookingDetails}>
                      <div className={classes.bookingInfo}>
                        <div
                          className={`${classes.bookingDate} ${classes.placeholder}`}
                        ></div>
                        <div
                          className={`${classes.bookingLocation} ${classes.placeholder}`}
                        ></div>
                      </div>
                      <div
                        className={`${classes.bookingAmount} ${classes.placeholder}`}
                      ></div>
                    </div>
                    <div className={classes.bookingActions}>
                      <div
                        className={`${classes.bookingActionButton} ${classes.placeholder}`}
                      ></div>
                    </div>
                  </div>
                ))
            ) : dashboardData.upcomingBookings?.length > 0 ? (
              dashboardData.upcomingBookings.map((booking) => (
                <div
                  key={booking._id || Math.random()}
                  className={classes.bookingCard}
                >
                  <div className={classes.bookingHeader}>
                    <div className={classes.bookingEvent}>
                      {booking.details}
                    </div>
                    <div
                      className={`${classes.bookingStatus} ${getStatusClass(
                        booking.status
                      )}`}
                    >
                      {booking.status}
                    </div>
                  </div>
                  <div className={classes.bookingDetails}>
                    <div className={classes.bookingInfo}>
                      <div className={classes.bookingDate}>
                        <span className={classes.infoIcon}>📅</span>{" "}
                        {new Date(booking.date).toLocaleDateString()}
                      </div>
                      <div className={classes.bookingLocation}>
                        <span className={classes.infoIcon}>📍</span>{" "}
                        {booking.address}
                      </div>
                    </div>
                    <div className={classes.bookingAmount}>
                      {formatCurrency(booking.amount)}
                    </div>
                  </div>
                  <div className={classes.bookingActions}>
                    <button
                      className={classes.bookingActionButton}
                      onClick={handleViewAllBtn}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className={classes.noBookings}>
                No upcoming bookings found
              </div>
            )}
          </div>
        </section>

        {/* Arena Advertisement */}
        <section className={classes.andArenaBox} onClick={handleArenaClick}>
          <img
            src={ARTIST_NEXT_DOOR_ARENA_ADVERT}
            className={classes.arenaImg}
            alt="Artist Arena"
          />
          <button
            className={classes.learnMoreButton}
            onClick={() => setShowHowItWorksModal(true)}
          >
            Learn How It Works
          </button>
        </section>
      </div>

      {/* How It Works Modal */}
      <HowItWorksArtistModal
        isOpen={showHowItWorksModal}
        onClose={() => setShowHowItWorksModal(false)}
      />
    </div>
  );
};

export default Home;
